import { Controller } from "@hotwired/stimulus";
import { track } from "../analytics";

export default class extends Controller {
  static targets = [
    "eventTypes",
    "dateInput",
    "timeSlots",
    "slotsLoading",
    "formSection",
    "eventTypeId",
    "startTime",
    "slotDisplayText",
    "submitButton",
  ];

  connect() {
    // Set minimum date to today
    const dateInput = this.dateInputTarget;
    if (dateInput) {
      const today = new Date().toISOString().split("T")[0];
      dateInput.min = today;
      dateInput.value = today;

      // Fetch slots for today by default
      this.fetchSlots(today);
    }

    // Auto-select first event type if none selected
    const selectedRadio = this.element.querySelector(
      'input[name="event_type"]:checked',
    );
    if (!selectedRadio) {
      const firstRadio = this.element.querySelector('input[name="event_type"]');
      if (firstRadio) {
        firstRadio.checked = true;
      }
    }

    // Track booking page view
    track("booking_page_viewed");
  }

  selectEventType(event) {
    // Remove highlight from all options
    const options = this.element.querySelectorAll(".peer-checked");
    options.forEach((opt) => {
      opt.classList.remove("border-lime", "bg-lime/5");
      opt.classList.add("border-neutral-200", "dark:border-neutral-700");
    });

    // Add highlight to selected
    const selectedDiv = event.target.closest('div[class*="peer-checked"]');
    if (selectedDiv) {
      selectedDiv.classList.remove(
        "border-neutral-200",
        "dark:border-neutral-700",
      );
      selectedDiv.classList.add("border-lime", "bg-lime/5");
    }

    // Track event type selection
    const eventTypeId = event.target.value;
    const eventTypeName = event.target
      .closest('div[class*="peer-checked"]')
      ?.querySelector("h3")
      ?.textContent?.trim();
    const eventTypeElement = this.element.querySelector(
      `input[value="${eventTypeId}"]`,
    );
    const duration = eventTypeElement?.dataset?.bookingDurationParam;
    const priceElement = event.target
      .closest('div[class*="peer-checked"]')
      ?.querySelector(".text-lg");
    const price = priceElement?.textContent?.trim();

    if (eventTypeId) {
      track("booking_event_type_selected", {
        event_type_id: eventTypeId,
        event_type_name: eventTypeName,
        duration_minutes: duration,
        price: price,
      });
    }

    // Refresh slots for selected date
    if (this.dateInputTarget.value) {
      this.fetchSlots(this.dateInputTarget.value);
    }
  }

  dateChanged(event) {
    const date = event.target.value;
    if (date) {
      // Track date selection
      track("booking_date_selected", {
        date: date,
      });

      this.fetchSlots(date);
    }
  }

  fetchSlots(date) {
    const eventTypeRadio = this.element.querySelector(
      'input[name="event_type"]:checked',
    );
    const eventTypeId = eventTypeRadio ? eventTypeRadio.value : "";

    // Show loading state
    this.timeSlotsTarget.classList.add("hidden");
    this.slotsLoadingTarget.classList.remove("hidden");

    // Build URL with params
    const params = new URLSearchParams({ date });
    if (eventTypeId) {
      params.append("event_type_id", eventTypeId);
    }

    fetch(`/booking/slots?${params.toString()}`)
      .then((response) => response.json())
      .then((data) => {
        this.renderSlots(data.slots || []);
      })
      .catch((error) => {
        console.error("Error fetching slots:", error);
        this.timeSlotsTarget.innerHTML =
          '<p class="text-red-500 text-sm text-center py-8">Error loading time slots. Please try again.</p>';
        this.timeSlotsTarget.classList.remove("hidden");
      })
      .finally(() => {
        this.slotsLoadingTarget.classList.add("hidden");
      });
  }

  renderSlots(slots) {
    this.timeSlotsTarget.classList.remove("hidden");

    if (slots.length === 0) {
      this.timeSlotsTarget.innerHTML = `
        <p class="text-neutral-400 dark:text-neutral-500 text-sm text-center py-8">
          No available slots for this date. Please select another date.
        </p>
      `;
      return;
    }

    const slotsHtml = slots
      .map(
        (slot) => `
      <label class="inline-block cursor-pointer">
        <input
          type="radio"
          name="time_slot"
          value="${slot.start_time}"
          class="peer sr-only"
          data-action="booking#selectSlot"
        >
        <span class="inline-block px-3 py-1.5 rounded-md border border-neutral-300 dark:border-neutral-600 text-sm font-medium text-neutral-700 dark:text-neutral-300 peer-checked:bg-lime peer-checked:text-charcoal peer-checked:border-lime hover:border-neutral-400 dark:hover:border-neutral-500 transition-colors">
          ${slot.formatted}
        </span>
      </label>
    `,
      )
      .join("");

    this.timeSlotsTarget.innerHTML = `
      <div class="flex flex-wrap gap-2">
        ${slotsHtml}
      </div>
    `;
  }

  selectSlot(event) {
    const startTime = event.target.value;
    const formattedTime = event.target.nextElementSibling.textContent;

    // Update form fields
    const eventTypeRadio = this.element.querySelector(
      'input[name="event_type"]:checked',
    );
    if (this.eventTypeIdTarget) {
      this.eventTypeIdTarget.value = eventTypeRadio ? eventTypeRadio.value : "";
    }
    if (this.startTimeTarget) {
      this.startTimeTarget.value = startTime;
    }

    // Track slot selection
    track("booking_slot_selected", {
      event_type_id: this.eventTypeIdTarget?.value,
      start_time: startTime,
      formatted_time: formattedTime,
      date: this.dateInputTarget?.value,
    });

    // Update display
    if (this.slotDisplayTextTarget) {
      const dateInput = this.dateInputTarget.value;
      const dateObj = new Date(dateInput);
      const formattedDate = dateObj.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
      this.slotDisplayTextTarget.textContent = `${formattedDate} at ${formattedTime}`;
    }

    // Show form and track form viewed
    this.formSectionTarget.classList.remove("hidden");
    track("booking_form_viewed", {
      event_type_id: this.eventTypeIdTarget?.value,
      start_time: startTime,
      date: this.dateInputTarget?.value,
    });
    if (this.submitButtonTarget) {
      this.submitButtonTarget.disabled = false;
    }
  }

  submitForm(event) {
    const eventTypeId = this.eventTypeIdTarget?.value;
    const startTime = this.startTimeTarget?.value;

    if (!eventTypeId || !startTime) {
      event.preventDefault();
      alert("Please select a meeting type and time slot.");
      return;
    }

    // Track form submission
    track("booking_submitted", {
      event_type_id: eventTypeId,
      start_time: startTime,
    });

    // Let the form submit normally
  }
}
